// FILE: src/lib/planLimits.js
// Copy/paste-ready instructions (frontend):
// 1) Set your live Stripe Price IDs via env vars; falling back to sandbox/test
//    IDs is no longer allowed so builds fail fast if values are missing.
// 2) Do NOT paste hosted checkout links (https://buy.stripe.com/...) because
//    those skip the custom Checkout session and the prorated upgrade credit
//    will not be applied.


const GB = 1024 * 1024 * 1024;

function loadPriceId(envVar) {
  const raw = process.env[envVar];
   if (!raw || !raw.trim()) {
    throw new Error(
      `[planLimits] Missing Stripe Price ID. Define ${envVar}=price_xxx in your .env (live mode) so the app never falls back to sandbox prices.`,
    );
  }


  // Reject hosted checkout links so the UI stays on the custom Checkout flow,
  // where the backend applies the prorated upgrade coupon.
  if (/^https?:\/\//i.test(raw)) {
    throw new Error(
      `[planLimits] ${envVar} must be a Stripe Price ID (price_...), not a hosted checkout link. Custom Checkout flow is required for prorated upgrades.`,
    );

  }

  return raw.trim();
}

export const PRICE_ID_BASIC = loadPriceId("REACT_APP_PRICE_BASIC");
export const PRICE_ID_STANDARD = loadPriceId("REACT_APP_PRICE_STANDARD");
export const PRICE_ID_ADVANCED = loadPriceId("REACT_APP_PRICE_ADVANCED");
export const PRICE_ID_FAMILY = loadPriceId("REACT_APP_PRICE_FAMILY");

export const PLAN_PRICE_LINKS = Object.freeze({
  basic: PRICE_ID_BASIC,
  standard: PRICE_ID_STANDARD,
  advanced: PRICE_ID_ADVANCED,
  family: PRICE_ID_FAMILY,
});

const EUR = "eur";

export const PLAN_CATALOG = [
  {
    key: "basic",
    backendKey: "basic",
    name: "Basic",
    priceId: PRICE_ID_BASIC,
    priceText: "€49.99",
    priceCents: 4999,
    currency: EUR,
    cadence: "/year",
    blurb: "Perfect for getting started.",
    accountManagers: 1,
    storageGB: 1,
    features: [
      "1 account manager",
      "1 GB secure storage",
      "Secure document vault",
      "Message vaults & funeral planning",
      "Email support",
    ],
    highlight: false,
  },
  {
    key: "standard",
    backendKey: "standard",
    name: "Standard",
    priceId: PRICE_ID_STANDARD,
    priceText: "€99.99",
    priceCents: 9999,
    currency: EUR,
    cadence: "/year",
    blurb: "More room for your family and documents.",
    accountManagers: 2,
    storageGB: 3,
    features: [
      "2 account managers",
      "3 GB secure storage",
      "Full digital vault & messaging",
      "Trusted contacts & notifications",
      "Priority email support",
    ],
    highlight: true,
  },
  {
    key: "advanced",
    backendKey: "advanced",
    name: "Advanced User",
    priceId: PRICE_ID_ADVANCED,
    priceText: "€149.99",
    priceCents: 14999,
    currency: EUR,
    cadence: "/year",
    blurb: "Maximum storage and manager access.",
    accountManagers: 5,
    storageGB: 10,
    features: [
      "5 account managers",
      "10 GB secure storage",
      "All Standard features",
      "Priority support",
      "Advanced coordination tools",
    ],
    highlight: false,
  },
  {
    key: "family",
    backendKey: "family",
    name: "Family Plan",
    priceId: PRICE_ID_FAMILY,
    priceText: "€199.99",
    priceCents: 19999,
    currency: EUR,
    cadence: "/year",
    blurb: "One plan for the whole family. Each member gets their own private account.",
    accountManagers: 3,
    storageGB: 5,
    features: [
      "Up to 5 family members",
      "Each member gets their own private account",
      "3 account managers per member",
      "5 GB secure storage per member",
      "Full digital vault & messaging per member",
      "1 family organiser manages invites",
      "Priority support",
    ],
    highlight: false,
  },
];

const PLAN_ALIASES = {
  pro: "advanced",
  premium: "advanced",
  plus: "advanced",
  starter: "basic",
  free: "basic",
  pending: "basic",
};

function normalizePlanKey(rawKey, fallback = "basic") {
  const key = String(rawKey || fallback).toLowerCase();
  return PLAN_ALIASES[key] || key;
}

function getPlanByKey(key) {
  const normalized = normalizePlanKey(key);
  return PLAN_CATALOG.find((p) => p.key === normalized) || null;
}

export function getPlanFromProfile(userProfile = {}) {
  const planKey =
    userProfile.subscriptionPlan ||
    userProfile.plan ||
    userProfile.backendPlan ||
    userProfile.planId ||
    userProfile.planKey ||
    "basic";

  return getPlanByKey(planKey) || PLAN_CATALOG[0];
}

export function getAccountManagerLimit(userProfile = {}) {
  const override =
    userProfile.planLimits?.accountManagers ??
    userProfile.planLimits?.maxAccountManagers ??
    userProfile.maxAccountManagers;

  if (Number.isFinite(override) && override > 0) return override;

  const plan = getPlanFromProfile(userProfile);
  return plan?.accountManagers ?? PLAN_CATALOG[0].accountManagers;
}

export function getStorageLimitBytes(userProfile = {}) {
  const overrideBytes =
    userProfile.planLimits?.maxStorageBytes ?? userProfile.maxStorageBytes;

  if (Number.isFinite(overrideBytes) && overrideBytes > 0) return overrideBytes;

  const plan = getPlanFromProfile(userProfile);
  const gb = plan?.storageGB ?? PLAN_CATALOG[0].storageGB;
  return (gb || 0) * GB;
}

export function isHostedCheckoutLink(value) {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (!/^https?:\/\//i.test(trimmed)) return false;
  try {
    const url = new URL(trimmed);
    return /(^|\.)stripe\.com$/i.test(url.hostname);
  } catch {
    return false;
  }
}

export function getCheckoutLinkForPlan(planKey) {
  const plan = getPlanByKey(planKey) || PLAN_CATALOG[0];
  return plan.priceId;
}
